import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { LogOut, Keyboard, Info, User as UserIcon, Palette, PlugZap } from 'lucide-react'
import { useAuthStore } from '@/stores/auth'
import { usePanelConexionStore } from '@/components/PanelConexion'
import { ThemeToggle } from '@/components/ThemeToggle'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { getInitials, avatarClasses } from '@/lib/identity'
import { cn } from '@/lib/utils'

interface Props {
  collapsed?: boolean
}

/**
 * Menú de usuario en el pie de la sidebar.
 *
 * Reemplaza el email suelto + botón logout del header horizontal anterior.
 * Patrón inspirado en AuthUserFooter de machette-tech (sidebar footer con
 * avatar y dropdown que se abre hacia arriba).
 *
 * §6.3 plan PWA premium.
 */
export function UserMenu({ collapsed = false }: Props) {
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const navigate = useNavigate()

  if (!user) return null

  const displayName = user.nombre?.trim() || user.email
  const initials = getInitials(displayName)
  const avatar = avatarClasses(displayName)

  function handleLogout() {
    logout()
    toast.success('Sesión cerrada')
    navigate('/login', { replace: true })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title={collapsed ? `${displayName} · ${user.email}` : undefined}
          className={cn(
            'flex w-full items-center gap-2.5 rounded-xl transition-colors hover:bg-sidebar-accent/60',
            collapsed ? 'h-12 justify-center p-0' : 'px-2.5 py-2 text-left',
          )}
          aria-label="Abrir menú de usuario"
        >
          <span className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-full font-semibold text-xs', avatar)}>
            {initials}
          </span>
          {!collapsed && (
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold text-sidebar-foreground">{displayName}</span>
              <span className="block truncate text-[11px] text-muted-foreground">{user.email}</span>
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align={collapsed ? 'center' : 'start'} className="w-56">
        <DropdownMenuLabel>Tu sesión</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => navigate('/select-co')}>
          <UserIcon className="h-4 w-4" />
          Cambiar de organización
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/app/branding')}>
          <Palette className="h-4 w-4" />
          Apariencia
        </DropdownMenuItem>
        {/* DC-2 (8f3a2596): panel de conexión — ubicación ratificada por el auditor. */}
        <DropdownMenuItem onClick={() => usePanelConexionStore.getState().setOpen(true)}>
          <PlugZap className="h-4 w-4" />
          Conexión
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/app/acerca-de')}>
          <Info className="h-4 w-4" />
          Acerca de
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/app/acerca-de#atajos')}>
          <Keyboard className="h-4 w-4" />
          Atajos de teclado
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <div className="px-2 py-1.5 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">Apariencia</span>
          <ThemeToggle />
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:bg-destructive/10 focus:text-destructive">
          <LogOut className="h-4 w-4" />
          Cerrar sesión
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
